import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Stack,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Checkbox,
  Button,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Home as HomeIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../../utils/config';
import { format } from 'date-fns';

interface User {
  id: string;
  name: string;
  email: string;
  company?: string;
}

interface Thread {
  id: number;
  subject: string;
  category: string | null;
  status: string;
  priority: string;
  user: User;
  assignedTo: {
    id: string;
    name: string;
  } | null;
  messageCount: number;
  adminUnreadCount: number;
  lastMessage: {
    content: string;
    senderType: string;
    createdAt: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

interface ThreadsResponse {
  threads: Thread[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  stats: {
    open: number;
    pending: number;
    resolved: number;
    closed: number;
    avgResponseTime: string;
  };
}

const MessagesThreadList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const [selectedThreads, setSelectedThreads] = useState<number[]>([]);
  
  // Tab to status mapping
  const tabToStatus: Record<number, string> = {
    0: 'open',      // Inbox
    1: 'pending',   // Awaiting User
    2: 'resolved',  // Resolved
    3: 'closed',    // Closed
  };

  // State from URL params
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '0'));
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(searchParams.get('limit') || '50'));
  const [currentTab, setCurrentTab] = useState(() => {
    const statusParam = searchParams.get('status');
    const tabIndex = Object.entries(tabToStatus).find(([, status]) => status === statusParam)?.[0];
    return tabIndex ? parseInt(tabIndex) : 0; // Default to Inbox tab
  });

  // Fetch overall stats (not filtered by current tab)
  const { data: statsData, refetch: refetchStats } = useQuery({
    queryKey: ['admin', 'messages', 'stats'],
    queryFn: async () => {
      const response = await authenticatedFetch(
        `${API_URL}/api/marketcompass/admin/messages/stats`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response?.ok) {
        throw new Error('Failed to fetch stats');
      }

      const result = await response.json();
      return result.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch threads
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'messages', 'threads', page, rowsPerPage, currentTab],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: (page + 1).toString(), // API uses 1-based pagination
        limit: rowsPerPage.toString(),
      });
      
      // Use status based on current tab
      const status = tabToStatus[currentTab];
      if (status) {
        params.append('status', status);
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/marketcompass/admin/messages/threads?${params}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response?.ok) {
        throw new Error('Failed to fetch threads');
      }

      const result = await response.json();
      return result.data as ThreadsResponse;
    },
  });

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    searchParams.set('page', newPage.toString());
    setSearchParams(searchParams);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    searchParams.set('limit', newLimit.toString());
    searchParams.set('page', '0');
    setSearchParams(searchParams);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setPage(0);
    setSelectedThreads([]); // Clear selections when changing tabs
    // Update URL params
    const status = tabToStatus[newValue];
    if (status) {
      searchParams.set('status', status);
    } else {
      searchParams.delete('status');
    }
    searchParams.set('page', '0');
    setSearchParams(searchParams);
  };

  // Clear selections when threads change
  useEffect(() => {
    setSelectedThreads([]);
  }, [data?.threads]);



  // Bulk close mutation - must be defined before any conditional returns
  const bulkCloseMutation = useMutation({
    mutationFn: async (threadIds: number[]) => {
      const promises = threadIds.map(threadId => 
        authenticatedFetch(
          `${API_URL}/api/marketcompass/admin/messages/threads/${threadId}/status`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ status: 'closed' }),
          }
        )
      );
      
      const responses = await Promise.all(promises);
      const failed = responses.filter(r => !r?.ok);
      if (failed.length > 0) {
        throw new Error(`Failed to close ${failed.length} thread(s)`);
      }
      return responses;
    },
    onSuccess: () => {
      setSelectedThreads([]);
      // Refetch threads and stats
      queryClient.invalidateQueries({ queryKey: ['admin', 'messages', 'threads'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'messages', 'stats'] });
    },
  });

  const handleBulkClose = () => {
    if (selectedThreads.length > 0) {
      bulkCloseMutation.mutate(selectedThreads);
    }
  };

  const getStatusColor = (status: string): 'info' | 'warning' | 'success' | 'default' => {
    switch (status) {
      case 'open':
        return 'warning';
      case 'pending':
        return 'info';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  const threads = data?.threads || [];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/')}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/admin/hub')}
          sx={{ cursor: 'pointer' }}
        >
          Admin Hub
        </Link>
        <Typography color="text.primary" variant="body2">
          Support Tickets
        </Typography>
      </Breadcrumbs>

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">
          Support Tickets
        </Typography>
        <IconButton onClick={() => { refetch(); refetchStats(); }} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Tabs for Status */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Inbox" />
          <Tab label="Awaiting User" />
          <Tab label="Resolved" />
          <Tab label="Closed" />
        </Tabs>
      </Paper>

      {/* Table or Empty State */}
      {isLoading ? (
        <Paper sx={{ position: 'relative', minHeight: 400 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Box>
        </Paper>
      ) : error ? (
        <Paper sx={{ p: 3 }}>
          <Alert severity="error">
            Failed to load messages. Please try again later.
          </Alert>
        </Paper>
      ) : currentTab === 0 && threads.length === 0 ? (
        <Paper sx={{ py: 8, px: 4 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              gap: 3,
            }}
          >
            <CheckCircleOutlineIcon
              sx={{
                fontSize: 120,
                color: 'success.main',
                opacity: 0.8,
              }}
            />
            <Box>
              <Typography variant="h5" gutterBottom color="text.primary">
                Empty Inbox!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500 }}>
                An empty inbox is what we strive for. Great job keeping up with support tickets!
              </Typography>
            </Box>
          </Box>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {currentTab === 2 && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={threads.length > 0 && selectedThreads.length === threads.length}
                        indeterminate={selectedThreads.length > 0 && selectedThreads.length < threads.length}
                        onChange={(event) => {
                          if (event.target.checked) {
                            setSelectedThreads(threads.map(t => t.id));
                          } else {
                            setSelectedThreads([]);
                          }
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell>Subject</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Messages</TableCell>
                  <TableCell>Last Activity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {threads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={currentTab === 2 ? 6 : 5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No tickets found in this category.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  threads.map((thread) => {
                    const isSelected = selectedThreads.includes(thread.id);
                    return (
                      <TableRow
                        key={thread.id}
                        hover
                        selected={isSelected}
                        onClick={(event) => {
                          // Prevent navigation if clicking checkbox
                          const target = event.target as HTMLElement;
                          const isCheckbox = target instanceof HTMLInputElement && target.type === 'checkbox';
                          if (!isCheckbox) {
                            // Preserve current URL params when navigating
                            const returnUrl = `/admin/support-tickets?${searchParams.toString()}`;
                            navigate(`/admin/support-tickets/${thread.id}?returnUrl=${encodeURIComponent(returnUrl)}`);
                          }
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        {currentTab === 2 && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected}
                              onChange={(event) => {
                                event.stopPropagation();
                                if (isSelected) {
                                  setSelectedThreads(selectedThreads.filter(id => id !== thread.id));
                                } else {
                                  setSelectedThreads([...selectedThreads, thread.id]);
                                }
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">
                              {thread.subject}
                            </Typography>
                            {thread.adminUnreadCount > 0 && (
                              <Chip
                                label={thread.adminUnreadCount}
                                size="small"
                                color="error"
                                sx={{ height: 20, minWidth: 20 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {thread.user.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {thread.user.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={thread.status}
                            size="small"
                            color={getStatusColor(thread.status)}
                          />
                        </TableCell>
                        <TableCell>{thread.messageCount}</TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {format(new Date(thread.updatedAt), 'MMM d, yyyy h:mm a')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={data?.pagination.total || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          {currentTab === 2 && selectedThreads.length > 0 && (
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleBulkClose}
                disabled={bulkCloseMutation.isPending}
              >
                {bulkCloseMutation.isPending 
                  ? `Closing ${selectedThreads.length} thread${selectedThreads.length > 1 ? 's' : ''}...` 
                  : `Close Selected Thread${selectedThreads.length > 1 ? 's' : ''} (${selectedThreads.length})`
                }
              </Button>
            </Box>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default MessagesThreadList;